import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

export interface BillingTab {
  /** Absolute path this tab navigates to. */
  to: string;
  label: string;
  /**
   * Also treat these path prefixes as this tab — a run detail page is
   * still the Runs tab, and losing the highlight when you open one
   * makes the strip look broken.
   */
  matches?: string[];
}

interface Props {
  tabs: BillingTab[];
}

/**
 * The tab strip for the billing area.
 *
 * Billing is a set of routes rather than another section stacked on the
 * HOA detail page, so the tabs are links and the browser back button
 * works. The styling matches the in-dialog strip in
 * `AccountActivityDialog`, which is the app's existing tab convention.
 */
const BillingTabs: React.FC<Props> = ({ tabs }) => {
  const { pathname } = useLocation();

  const isCurrent = (tab: BillingTab): boolean =>
    pathname === tab.to ||
    (tab.matches ?? []).some((prefix) => pathname.startsWith(prefix));

  return (
    <div className="flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-700 mb-6">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          className={clsx(
            'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            isCurrent(tab)
              ? 'border-sky-500 text-sky-600 dark:text-sky-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
          )}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
};

export default BillingTabs;
